import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Avatar,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { Send, Trash2, MapPin, FileText, CheckCircle, Armchair, X, UserPlus, Undo2, XCircle } from 'lucide-react';

const GuestListTable = ({
  invitations = [],
  isCreator = false,
  selectedInvitations = {},
  toggleInvitationSelection = () => { },
  toggleSelectAll = () => { },
  onSendForm = () => { },
  onManageForms = () => { },
  onRespondToInvitation = () => { },
  onAssignSeat = () => { },
  onDelete = () => { },
  onViewResponse = () => { },
  onExport = () => { },
  onAddGuest = () => { },
  onRequestToAttend = () => { },
  isExporting = false,
  selectedFormId = null,
  responseCount = 0,
  event = {},
  currentUserEmail = '',
  currentUser = null,
  forms = [],
}) => {
  // console.log('🔍 GuestListTable RECEIVED:', {
  //   isCreator,
  //   invitationsCount: invitations.length,
  //   invitations: invitations.map(inv => ({
  //     id: inv.id,
  //     recipient_email: inv.recipient_email,
  //     recipient: inv.recipient,
  //     status: inv.status
  //   })),
  //   currentUserEmail
  // });

  const [hoveredRow, setHoveredRow] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);
  const [exportFormat, setExportFormat] = React.useState(null);
  const [exportAnchorEl, setExportAnchorEl] = React.useState(null);
  const [loadingResponses, setLoadingResponses] = React.useState({});
  const isExportMenuOpen = Boolean(exportAnchorEl);

  // Check if any invitation has a seat number
  const shouldShowSeatNumbers = invitations.some(inv => inv.seat_number);
  const shouldShowSeatActions = isCreator && event?.has_seating;
  const shouldShowFormStatus = isCreator || invitations.some(inv => inv.form_status !== undefined);
  const isGuestView = !isCreator;

  // Check if all accepted guests have already received form invitations
  const allGuestsHaveFormInvitations = invitations.filter(inv => inv.status === 'accepted').length > 0 && 
    invitations.filter(inv => inv.status === 'accepted').every(inv => inv.form_status && inv.form_status !== 'not_sent');

  // Check if there are any forms available for this event
  const hasForms = selectedFormId || (forms && forms.length > 0);

  const handleRowHover = (invitationId) => {
    setHoveredRow(invitationId);
  };

  const handleRowLeave = () => {
    setHoveredRow(null);
  };

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportFormat = (format) => {
    setExportAnchorEl(null);
    onExport(format);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      setExportFormat(format);
      setExportAnchorEl(null);
      await onExport(format);
    } catch (error) {
      // console.error('Export failed:', error);
    } finally {
      setExporting(false);
      setExportFormat(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Pending', color: 'warning' },
      'accepted': { label: 'Accepted', color: 'success' },
      'rejected': { label: 'Rejected', color: 'error' },
      'withdrawn': { label: 'Withdrawn', color: 'default' }
    };

    const statusInfo = statusMap[status] || { label: status, color: 'default' };

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color}
        size="small"
        className="capitalize"
      />
    );
  };

  const getFormStatusBadge = (invitation) => {
    // Show form status for any invitation that has form_status data
    if (invitation.form_status === undefined) {
      return null;
    }

    const hasResponse = invitation.form_submission_id || invitation.form_status === 'completed';

    const statusMap = {
      'pending': { 
        label: 'Pending', 
        color: 'warning', 
        icon: null 
      },
      'not_sent': { label: 'Not Sent', color: 'default', icon: null },
      'sent': {
        label: hasResponse ? 'Response Received' : 'Sent - Waiting',
        color: hasResponse ? 'success' : 'info',
        icon: hasResponse ? <CheckCircle size={16} /> : <Send size={16} />
      },
      'viewed': {
        label: hasResponse ? 'Response Received' : 'Viewed',
        color: hasResponse ? 'success' : 'primary',
        icon: hasResponse ? <CheckCircle size={16} /> : <FileText size={16} />
      },
      'in_progress': {
        label: hasResponse ? 'Response Received' : 'In Progress',
        color: hasResponse ? 'success' : 'warning',
        icon: hasResponse ? <CheckCircle size={16} /> : <CircularProgress size={14} />
      },
      'completed': {
        label: 'Response Received',
        color: 'success',
        icon: <CheckCircle size={16} />
      }
    };

    const status = hasResponse ? 'completed' : (invitation.form_status || 'not_sent');
    const statusInfo = statusMap[status] || { label: status, color: 'default', icon: null };

    return (
      <Tooltip
        title={statusInfo.label}
        arrow
        placement="top"
      >
        <Chip
          label={statusInfo.label}
          color={statusInfo.color}
          size="small"
          icon={statusInfo.icon}
          className="capitalize cursor-help"
        />
      </Tooltip>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <Box className="overflow-x-auto">
        {isCreator && (
          <div className="flex justify-end mb-4 space-x-2 px-4">
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<UserPlus size={16} />}
              onClick={() => onAddGuest()}
            >
              Add Guest
            </Button>

            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<Send size={16} />}
              onClick={() => onSendForm()}
              disabled={allGuestsHaveFormInvitations}
              className={allGuestsHaveFormInvitations ? 'opacity-50 cursor-not-allowed' : ''}
              title={allGuestsHaveFormInvitations ? 'All guests have already received form invitations' : 'Send form to guests'}
            >
              Send Form
            </Button>

            {hasForms && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileText size={16} />}
                onClick={() => onManageForms && onManageForms()}
                title="Manage forms (view, edit, create)"
              >
                Manage Forms
              </Button>
            )}

            <Button
              variant="outlined"
              size="small"
              startIcon={<FileText size={16} />}
              endIcon={<svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>}
              onClick={handleExportClick}
              disabled={exporting || responseCount === 0}
              aria-controls={isExportMenuOpen ? 'export-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isExportMenuOpen ? 'true' : undefined}
              className={responseCount === 0 ? 'text-gray-600 border-gray-600 cursor-not-allowed opacity-70' : ''}
            >
              {exporting ? (
                <span className="flex items-center">
                  <CircularProgress size={16} className="mr-2" />
                  Exporting...
                </span>
              ) : (
                <span className="flex items-center">
                  Export Responses
                  {responseCount > 0 && (
                    <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {responseCount} {responseCount === 1 ? 'response' : 'responses'}
                    </span>
                  )}
                </span>
              )}
            </Button>

            <Menu
              id="export-menu"
              anchorEl={exportAnchorEl}
              open={isExportMenuOpen}
              onClose={handleExportClose}
              MenuListProps={{
                'aria-labelledby': 'export-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleExport('excel')}>
                <FileText size={16} className="mr-2 text-green-600" />
                Export as Excel
              </MenuItem>
              <MenuItem onClick={() => handleExport('pdf')}>
                <FileText size={16} className="mr-2 text-red-600" />
                Export as PDF
              </MenuItem>
            </Menu>
          </div>
        )}
        <Table>
          <TableHead className="bg-gray-50 border-b border-gray-200">
            <TableRow>
              {isCreator && (
                <TableCell className="w-10 px-2 py-4">
                  <Checkbox
                    checked={Object.keys(selectedInvitations).length > 0 &&
                      Object.keys(selectedInvitations).length === invitations.length}
                    onChange={toggleSelectAll}
                    color="primary"
                  />
                </TableCell>
              )}
              <TableCell className="px-6 py-4 text-left text-xs font-medium text-gray-500 tracking-wider">
                {isCreator ? 'Guest' : 'Your Information'}
              </TableCell>
              <TableCell className="px-6 py-4 text-left text-xs font-medium text-gray-500 tracking-wider">
                Invitation Status
              </TableCell>
              {shouldShowFormStatus && (
                <TableCell className="px-6 py-4 text-left text-xs font-medium text-gray-500 tracking-wider">
                  Form Response
                </TableCell>
              )}
              {shouldShowSeatNumbers && (
                <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  Seat No.
                </TableCell>
              )}
              <TableCell className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="divide-y divide-gray-200">
            {invitations.map((invitation) => (
              <TableRow
                key={invitation.id}
                className={`hover:bg-gray-50 transition-colors duration-150 ${selectedInvitations[invitation.id] ? 'bg-blue-50' : ''}`}
                onMouseEnter={() => handleRowHover(invitation.id)}
                onMouseLeave={handleRowLeave}
              >
                {isCreator && (
                  <TableCell className="px-2 py-4">
                    <Checkbox
                      checked={!!selectedInvitations[invitation.id]}
                      onChange={() => toggleInvitationSelection(invitation.id)}
                      color="primary"
                    />
                  </TableCell>
                )}
                <TableCell className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Avatar
                      src={invitation.recipient?.avatar_url}
                      alt={invitation.recipient_name || invitation.recipient?.name}
                      className="h-8 w-8 rounded-full mr-3"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {invitation.recipient_name || invitation.recipient?.name || 'Guest'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {invitation.recipient_email || invitation.recipient?.email || 'No email provided'}
                      </div>
                      {invitation.form_sent_at && (
                        <div className="text-xs text-gray-400 mt-1">
                          Form sent: {new Date(invitation.form_sent_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(invitation.status)}
                </TableCell>
                {shouldShowFormStatus && (!isGuestView || invitation.form_sent_at) && (
                  <TableCell className="px-6 py-4 whitespace-nowrap">
                    {getFormStatusBadge(invitation)}
                  </TableCell>
                )}
                {shouldShowSeatNumbers && (
                  <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {invitation.seat_number || (invitation.guest?.seat_number) || '-'}
                  </TableCell>
                )}
                <TableCell className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {console.log('🔍 ACTIONS SECTION DEBUG:', {
                      invitationId: invitation.id,
                      isCreator: isCreator,
                      sender: invitation.sender,
                      currentUserId: currentUser?.id,
                      currentUserEmail: currentUserEmail
                    })}
                    {console.log('🔍 WHICH SECTION:', isCreator ? 'CREATOR ACTIONS' : 'GUEST ACTIONS')}
                    {isCreator ? (
                      // Creator actions
                      <>
                        {(() => {
                          // Check if current user is the sender of this invitation
                          const isSender = invitation.sender?.toString() === currentUser?.id?.toString();
                          
                          console.log('🔍 CREATOR ACTIONS - isSender check:', {
                            invitationId: invitation.id,
                            sender: invitation.sender,
                            currentUserId: currentUser?.id,
                            isSender: isSender,
                            status: invitation.status
                          });
                          
                          // If current user is the sender, show sender-specific actions
                          if (isSender) {
                            return (
                              <>
                                {/* Send Form - Always show, Disabled if pending, Enabled if accepted */}
                                <Tooltip title={invitation.status === 'pending' ? "Cannot send form until invitation is accepted" : (invitation.form_status && invitation.form_status !== 'not_sent' ? "Form Already Sent" : "Send Form")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      if (invitation.status === 'pending') return; // Prevent action if pending
                                      if (invitation.form_status && invitation.form_status !== 'not_sent') return; // Prevent action if form invitation exists
                                      onSendForm([invitation]);
                                    }}
                                    disabled={invitation.status === 'pending' || !!(invitation.form_status && invitation.form_status !== 'not_sent')}
                                    className={`
                                      ${invitation.status === 'pending' || (invitation.form_status && invitation.form_status !== 'not_sent')
                                        ? 'text-gray-500 cursor-not-allowed opacity-60' 
                                        : 'text-green-500 hover:bg-green-50'
                                      }
                                    `}
                                  >
                                    <Send size={16} />
                                  </IconButton>
                                </Tooltip>
                                
                                {/* Assign Seat - Always show, Disabled if pending, Enabled if accepted */}
                                <Tooltip title={invitation.status === 'pending' ? "Cannot assign seat until invitation is accepted" : "Assign Seat"}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (invitation.status === 'pending') return; // Prevent action if pending
                                      onAssignSeat(invitation);
                                    }}
                                    disabled={invitation.status === 'pending'}
                                    className={`
                                      ${invitation.status === 'pending'
                                        ? 'text-gray-500 cursor-not-allowed opacity-60' 
                                        : 'text-blue-500 hover:bg-blue-50'
                                      }
                                    `}
                                  >
                                    <Armchair size={16} />
                                  </IconButton>
                                </Tooltip>
                                
                                {/* Delete button - Always available for sender */}
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => onDelete(invitation)}
                                    className="text-red-500 hover:bg-red-50"
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            );
                          }
                          
                          // If current user is NOT the sender, show regular creator actions
                          return (
                            <>
                              {/* Regular creator actions for invitations sent by others */}
                              {invitation.status === 'accepted' && (
                                <Tooltip title="Assign Seat">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onAssignSeat(invitation);
                                    }}
                                    className="text-blue-500 hover:bg-blue-50"
                                  >
                                    <Armchair size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {invitation.status === 'accepted' && (
                                <Tooltip title={invitation.form_status && invitation.form_status !== 'not_sent' ? "Form Already Sent" : "Send Form"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      if (invitation.form_status && invitation.form_status !== 'not_sent') {
                                        return; // Prevent action if form invitation exists
                                      }
                                      onSendForm([invitation]);
                                    }}
                                    disabled={!!(invitation.form_status && invitation.form_status !== 'not_sent')}
                                    className={`
                                      ${(invitation.form_status && invitation.form_status !== 'not_sent')
                                        ? 'text-gray-500 cursor-not-allowed opacity-60' 
                                        : 'text-green-500 hover:bg-green-50'
                                      }
                                    `}
                                  >
                                    <Send size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => onDelete(invitation)}
                                  className="text-red-500 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </Tooltip>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      // Guest actions 
                      <div className="flex flex-col gap-2">
                        {(() => {
                          // Debug: Log invitation data and current user
                          
                          // Check if current user is the sender (initiated the invitation/request)
                          const isSender = invitation.sender?.toString() === currentUser?.id?.toString();
                          
                          // Debug sender comparison
                          
                          // Check if current user is the recipient (received the invitation/request)
                          const isRecipient = invitation.recipient?.toString() === currentUser?.id?.toString() ||
                                             invitation.recipient_email?.toLowerCase() === currentUserEmail?.toLowerCase();
                          
                          // Debug recipient comparison
                          
                          // If sender and pending, show withdraw option
                          if (isSender && invitation.status === 'pending') {
                            return (
                              <Button
                                variant="text"
                                size="small"
                                color="primary"
                                startIcon={
                                  loadingResponses[invitation.id] === 'withdraw' ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <Undo2 size={14} />
                                  )
                                }
                                onClick={async () => {
                                  setLoadingResponses(prev => ({ ...prev, [invitation.id]: 'withdraw' }));
                                  try {
                                    await onRespondToInvitation(invitation.id, 'withdraw');
                                  } finally {
                                    setLoadingResponses(prev => {
                                      const newState = { ...prev };
                                      delete newState[invitation.id];
                                      return newState;
                                    });
                                  }
                                }}
                                disabled={!!loadingResponses[invitation.id]}
                                className="text-xs p-0 h-auto min-w-0 normal-case"
                              >
                                {loadingResponses[invitation.id] === 'withdraw' ? 'Processing...' : 'Withdraw'}
                              </Button>
                            );
                          }
                          
                          // If recipient and pending, show accept/reject options
                          if (isRecipient && invitation.status === 'pending') {
                            return (
                              <div className="flex gap-2">
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  startIcon={
                                    loadingResponses[invitation.id] === 'accept' ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <CheckCircle size={16} />
                                    )
                                  }
                                  onClick={async () => {
                                    setLoadingResponses(prev => ({ ...prev, [invitation.id]: 'accept' }));
                                    try {
                                      await onRespondToInvitation(invitation.id, 'accept');
                                    } finally {
                                      setLoadingResponses(prev => {
                                        const newState = { ...prev };
                                        delete newState[invitation.id];
                                        return newState;
                                      });
                                    }
                                  }}
                                  disabled={!!loadingResponses[invitation.id]}
                                  className="text-xs p-0 h-auto min-w-0 normal-case"
                                >
                                  {loadingResponses[invitation.id] === 'accept' ? 'Processing...' : 'Accept'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  startIcon={
                                    loadingResponses[invitation.id] === 'reject' ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <XCircle size={16} />
                                    )
                                  }
                                  onClick={async () => {
                                    setLoadingResponses(prev => ({ ...prev, [invitation.id]: 'reject' }));
                                    try {
                                      await onRespondToInvitation(invitation.id, 'reject');
                                    } finally {
                                      setLoadingResponses(prev => {
                                        const newState = { ...prev };
                                        delete newState[invitation.id];
                                        return newState;
                                      });
                                    }
                                  }}
                                  disabled={!!loadingResponses[invitation.id]}
                                  className="text-xs p-0 h-auto min-w-0 normal-case"
                                >
                                  {loadingResponses[invitation.id] === 'reject' ? 'Processing...' : 'Reject'}
                                </Button>
                              </div>
                            );
                          }
                          
                          // If already responded, show status
                          if (invitation.status !== 'pending') {
                            return (
                              <span className="text-xs text-gray-500">
                                {invitation.status === 'accepted' ? '✓ Accepted' : 
                                 invitation.status === 'withdrawn' ? '✗ Withdrawn' : '✗ Rejected'}
                              </span>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {invitations.length === 0 && (
              <TableRow>
                <TableCell colSpan={isCreator ? 5 : 4} className="px-6 py-8 text-center">
                  {isCreator ? (
                    <div>
                      <p className="text-gray-500 mb-4">No guests found</p>
                      <Button
                        variant="contained"
                        startIcon={<UserPlus size={16} />}
                        onClick={onAddGuest}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Add Guest
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-500 mb-4">You haven't requested to attend this event yet.</p>
                      <Button
                        variant="contained"
                        startIcon={<UserPlus size={16} />}
                        onClick={onRequestToAttend}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Request to Attend
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {selectedFormId && isCreator && responseCount > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileText size={16} />}
            onClick={handleExportClick}
            disabled={isExporting}
            aria-controls={isExportMenuOpen ? 'export-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isExportMenuOpen ? 'true' : undefined}
          >
            {isExporting ? (
              <span className="flex items-center">
                <CircularProgress size={16} className="mr-2" />
                Exporting...
              </span>
            ) : `Export Responses (${responseCount})`}
          </Button>

          <Menu
            id="export-menu"
            anchorEl={exportAnchorEl}
            open={isExportMenuOpen}
            onClose={() => setExportAnchorEl(null)}
            MenuListProps={{
              'aria-labelledby': 'export-button',
            }}
          >
            <MenuItem onClick={() => handleExportFormat('excel')}>
              Export as Excel
            </MenuItem>
            <MenuItem onClick={() => handleExportFormat('pdf')}>
              Export as PDF
            </MenuItem>
          </Menu>
        </div>
      )}
    </div>
  );
};

export default GuestListTable;
